import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { OnboardingSetupService, SetupTask } from './onboarding-setup.service';

@Component({
  selector: 'app-onboarding-setup-widget',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule, MatButtonModule, MatProgressBarModule],
  template: `
    <div class="setup-widget-card" *ngIf="tasks.length > 0 && !isFullySetup">
      <div class="widget-header">
        <div class="header-text">
          <h2><mat-icon>rocket_launch</mat-icon> Welcome to e-Likita</h2>
          <p>Let's get your hospital ready. Complete these essential steps to start using the system.</p>
        </div>
        <div class="progress-section">
          <span class="progress-text">{{ completedCount }} of {{ tasks.length }} completed</span>
          <mat-progress-bar mode="determinate" [value]="progressPercentage"></mat-progress-bar>
        </div>
      </div>
      
      <div class="widget-tasks">
        <div class="setup-task" *ngFor="let task of tasks" [class.completed]="task.isCompleted">
          <div class="task-status">
            <mat-icon *ngIf="!task.isCompleted" class="pending-icon">radio_button_unchecked</mat-icon>
            <mat-icon *ngIf="task.isCompleted" class="success-icon">check_circle</mat-icon>
          </div>
          <div class="task-content">
            <h3>{{ task.title }}</h3>
            <p>{{ task.description }}</p>
          </div>
          <div class="task-action">
            <a *ngIf="!task.isCompleted" mat-stroked-button color="primary" [routerLink]="task.link">Go to {{ task.title }}</a>
            <span *ngIf="task.isCompleted" class="completed-text">Completed</span>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .setup-widget-card {
      background: var(--mat-sys-surface);
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 24px;
      border: 1px solid var(--mat-sys-outline-variant);
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }
    .widget-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      flex-wrap: wrap;
      gap: 16px;
    }
    .header-text h2 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 8px 0;
      font-size: 20px;
      color: var(--mat-sys-on-surface);
    }
    .header-text h2 mat-icon {
      color: var(--mat-sys-primary);
    }
    .header-text p {
      margin: 0;
      color: var(--mat-sys-on-surface-variant);
      font-size: 14px;
    }
    .progress-section {
      width: 200px;
      text-align: right;
    }
    .progress-text {
      display: block;
      margin-bottom: 8px;
      font-size: 13px;
      font-weight: 500;
      color: var(--mat-sys-on-surface-variant);
    }
    .widget-tasks {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .setup-task {
      display: flex;
      align-items: center;
      padding: 16px;
      border-radius: 12px;
      background: var(--mat-sys-surface-container-lowest);
      border: 1px solid var(--mat-sys-outline-variant);
      transition: background-color 0.2s;
    }
    .setup-task:hover {
      background: var(--mat-sys-surface-container-low);
    }
    .setup-task.completed {
      opacity: 0.7;
      background: var(--mat-sys-surface-container-lowest);
    }
    .task-status {
      margin-right: 16px;
    }
    .pending-icon {
      color: var(--mat-sys-on-surface-variant);
      font-size: 28px;
      width: 28px;
      height: 28px;
    }
    .success-icon {
      color: #4CAF50;
      font-size: 28px;
      width: 28px;
      height: 28px;
    }
    .task-content {
      flex: 1;
    }
    .task-content h3 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 500;
    }
    .task-content p {
      margin: 0;
      font-size: 14px;
      color: var(--mat-sys-on-surface-variant);
    }
    .task-action {
      margin-left: 16px;
    }
    .completed-text {
      color: #4CAF50;
      font-weight: 500;
      font-size: 14px;
      padding: 0 16px;
    }
    @media (max-width: 600px) {
      .setup-task {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }
      .task-action {
        margin-left: 0;
        width: 100%;
      }
      .task-action a {
        width: 100%;
      }
    }
  `]
})
export class OnboardingSetupWidgetComponent implements OnInit {
  private setupService = inject(OnboardingSetupService);
  
  tasks: SetupTask[] = [];
  completedCount = 0;
  progressPercentage = 0;
  isFullySetup = false;

  ngOnInit() {
    this.setupService.refreshTasks();
    
    this.setupService.tasks$.subscribe(tasks => {
      this.tasks = tasks;
      this.completedCount = tasks.filter(t => t.isCompleted).length;
      this.progressPercentage = tasks.length > 0 ? (this.completedCount / tasks.length) * 100 : 0;
      this.isFullySetup = tasks.length > 0 && this.completedCount === tasks.length;
    });
  }
}
